import { Feature } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { FilterIcon } from '../Icons';
import { Portal } from '../Portal';
import { useTranslation } from 'react-i18next';
import { defaultFeatureFilter } from '../../constants';
import { formatLanName } from '../../lan-coverage/lan-coverage';

export function FilterFeatures(props: {
    points: Feature[];
    featureFilter: string[];
    zoomToFiltered: boolean;
    onChangeFilter: (newFeatureFilter: string[]) => void;
    onToggleZoomToFiltered: () => void;
    showZoomToggle?: boolean;
    tiny?: boolean;
    horizontal?: boolean;
    allAreasLabel?: string | null;
}) {
    const showZoomToggle = props.showZoomToggle ?? false;
    const horizontal = props.horizontal ?? false;
    const tiny = props.tiny ?? false;

    const { t } = useTranslation();
    const allAreasLabel = props.allAreasLabel
        ? formatLanName(props.allAreasLabel)
        : t('stockholms-lan', { defaultValue: 'Stockholm' });

    const [showFilterModal, setShowFilterModal] = useState(false);
    const fallbackFilter = [defaultFeatureFilter].filter(Boolean);
    const initialFilter = [props.featureFilter, fallbackFilter].find(
        (filter) => filter.length > 0
    );
    const [rememberedFilter, setRememberedFilter] = useState<string[]>(() =>
        initialFilter ?? []
    );
    const triggerRef = useRef<HTMLButtonElement>(null);

    function handleClick(value: string) {
        const newFilter = props.featureFilter.includes(value)
            ? props.featureFilter.filter((v) => v !== value)
            : [...props.featureFilter, value];

        handleChange(newFilter);
    }

    function handleChange(newFilter: string[]) {
        if (newFilter.length > 0 && !isEqual(newFilter, rememberedFilter)) {
            setRememberedFilter(newFilter);
        }
        if (props.onChangeFilter) {
            props.onChangeFilter(newFilter);
        }
    }

    function handleClose() {
        setShowFilterModal(false);
        triggerRef.current?.focus();
    }

    const kommunSet = new Set<string>();
    for (const f of props.points) {
        kommunSet.add(f.properties?.kommunnamn);
    }

    const kommuner = Array.from(kommunSet);
    const hasDefaultFilter =
        defaultFeatureFilter.length > 0 && kommuner.includes(defaultFeatureFilter);
    const canRestoreRememberedFilter =
        rememberedFilter.length > 0 &&
        rememberedFilter.every((filter) => kommuner.includes(filter));

    const quickSelects = [
        ...(canRestoreRememberedFilter
            ? [
                  {
                      label:
                          rememberedFilter.length === 1
                              ? rememberedFilter[0]
                              : t('valda-omraden', { defaultValue: 'Valda områden' }),
                      value: rememberedFilter,
                  },
              ]
            : []),
        { label: allAreasLabel, value: [] },
    ];

    const editFilterLabel = t('andra-urval', {
        defaultValue: 'Ändra urval...',
    });
    const zoomStatusLabel = t(
        props.zoomToFiltered ? 'karta-graf-zoom' : 'karta-graf-zoom-av',
        {
            defaultValue: props.zoomToFiltered
                ? 'Karta/graf zoomas till urval'
                : 'Karta/graf zoomas inte till urval',
        }
    );

    const activeFilterCount = props.featureFilter.length;
    const isAllAreasSelected = activeFilterCount === 0;
    const selectedLabel =
        rememberedFilter.length === 1
            ? rememberedFilter[0]
            : t('valda-omraden', { defaultValue: 'Valda områden' });
    const selectedAreasLabel =
        activeFilterCount > 3
            ? `${props.featureFilter.slice(0, 3).join(', ')} ${t('och-flera-till', {
                  count: activeFilterCount - 3,
                  defaultValue: `och ${activeFilterCount - 3} till`,
              })}`
            : props.featureFilter.join(', ');

    return (
        <>
            <div
                className={`flex flex-col gap-1.5 ${
                    tiny ? 'items-center gap-3' : ''
                }`}
            >
                {!tiny && (
                    <div
                        className={
                            horizontal
                                ? 'w-full max-w-md rounded-lg bg-base-100 px-3.5 py-3 shadow-sm'
                                : 'w-full'
                        }
                    >
                        <span className="block text-[10px] font-semibold text-base-content/70">
                            {t('visa-data-for', { defaultValue: 'Visa data för' })}
                        </span>
                        <div className="tabs tabs-boxed mt-1.5 flex w-full">
                                {(!isAllAreasSelected || canRestoreRememberedFilter) && <button
                                    ref={triggerRef}
                                    type="button"
                                    role="tab"
                                    aria-selected={!isAllAreasSelected}
                                    onClick={() => handleChange(rememberedFilter)}
                                    className={`tab min-w-0 flex-1 gap-2 text-xs ${
                                        !isAllAreasSelected ? 'tab-active' : ''
                                    }`}
                                >
                                    <span className="truncate">{selectedLabel}</span>
                                    {rememberedFilter.length > 1 && (
                                        <span className="grid h-5 min-w-5 place-items-center rounded-full bg-base-100 px-1 text-[10px] text-primary">
                                            {rememberedFilter.length}
                                        </span>
                                    )}
                                </button>}
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isAllAreasSelected}
                                    onClick={() => handleChange([])}
                                    className={`tab flex-1 text-xs ${
                                        isAllAreasSelected ? 'tab-active' : ''
                                    }`}
                                >
                                    {allAreasLabel}
                                </button>
                        </div>
                        {activeFilterCount > 1 && (
                            <p className="mt-2 text-[10px] text-base-content/80">
                                {selectedAreasLabel}
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowFilterModal(true)}
                            className="mt-2 flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
                        >
                            <ListIcon />
                            {t('andra-urval', { defaultValue: 'Ändra urval...' })}
                        </button>
                        {showZoomToggle && (
                            <>
                                <div className="my-3 border-t border-base-content/15" />
                                <label
                                    aria-label={t('zooma-till-urval')}
                                    className="flex cursor-pointer items-center gap-2.5"
                                >
                                    <input
                                        type="checkbox"
                                        checked={props.zoomToFiltered}
                                        onChange={() => props.onToggleZoomToFiltered()}
                                        className="toggle toggle-sm toggle-primary"
                                    />
                                    <span className="flex flex-col leading-tight">
                                        <span className="text-xs font-semibold">
                                            {t('zooma-till-urval')}
                                        </span>
                                        <span className="text-[10px] text-base-content/70">
                                            {t(
                                                props.zoomToFiltered
                                                    ? 'karta-graf-zoom'
                                                    : 'karta-graf-zoom-av',
                                                {
                                                    defaultValue: props.zoomToFiltered
                                                        ? 'Karta/graf zoomas till urval'
                                                        : 'Karta/graf zoomas inte till urval',
                                                }
                                            )}
                                        </span>
                                    </span>
                                </label>
                            </>
                        )}
                    </div>
                )}
                {tiny && (
                    <div
                        className="tooltip tooltip-right"
                        data-tip={editFilterLabel}
                    >
                        <button
                            ref={triggerRef}
                            type="button"
                            onClick={() => {
                                setShowFilterModal(true);
                            }}
                            aria-label={editFilterLabel}
                            className="btn btn-sm btn-primary grid place-items-center w-full aspect-square"
                        >
                            <FilterIcon size={24} fill="white" />
                        </button>
                    </div>
                )}
                {tiny && (
                    <div className={`tabs tabs-xs tabs-boxed flex flex-col`}>
                        {quickSelects.map((q) => {
                            const isSelected = q.value.length > 0
                                ? props.featureFilter.length > 0
                                : props.featureFilter.length === 0;
                            const compactLabel = q.value.length > 0
                                ? rememberedFilter.length > 1
                                    ? rememberedFilter.length
                                    : (rememberedFilter[0] ?? q.label)[0]
                                : q.label[0];
                            return (
                                <button
                                    key={q.label}
                                    type="button"
                                    onClick={() => {
                                        handleChange(
                                            q.value.length > 0
                                                ? rememberedFilter
                                                : q.value
                                        );
                                    }}
                                    aria-label={q.label}
                                    className={`tooltip tooltip-right tab tab-xs ${isSelected ? 'tab-active' : ''}`}
                                    data-tip={
                                        q.value.length > 0
                                            ? selectedAreasLabel || selectedLabel
                                            : q.label
                                    }
                                >
                                    {compactLabel}
                                </button>
                            );
                        })}
                    </div>
                )}
                {tiny && showZoomToggle && (
                    <div
                        className="tooltip tooltip-right"
                        data-tip={`${t('zooma-till-urval')}: ${zoomStatusLabel}`}
                    >
                        <label
                            aria-label={t('zooma-till-urval')}
                            className="flex flex-row gap-2 text-sm items-center"
                        >
                            <input
                                type="checkbox"
                                checked={props.zoomToFiltered}
                                onChange={() => props.onToggleZoomToFiltered()}
                                className="checkbox checkbox-primary checkbox-xs"
                            />
                        </label>
                    </div>
                )}
            </div>
            {showFilterModal && (
                <Portal>
                    <FilterModal
                        featureFilter={props.featureFilter}
                        onClose={handleClose}
                        kommuner={kommuner}
                        onClick={handleClick}
                        onClear={() => {
                            const resetFilter =
                                defaultFeatureFilter && hasDefaultFilter
                                    ? [defaultFeatureFilter]
                                    : [];
                            setRememberedFilter(resetFilter);
                            handleChange(resetFilter);
                            handleClose();
                        }}
                    />
                </Portal>
            )}
        </>
    );
}

function ListIcon() {
    return (
        <svg
            aria-hidden="true"
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 16 16"
        >
            <path
                d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.5"
            />
        </svg>
    );
}

function FilterModal(props: {
    onClose: () => void;
    kommuner: string[];
    featureFilter: string[];
    onClick: (value: string) => void;
    onClear: () => void;
}) {
    const { t } = useTranslation();
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        cardRef.current?.focus();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                props.onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [props.onClose]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-20 isolate grid place-items-center pointer-events-none z-50">
            <div
                className="fixed inset-0 pointer-events-auto cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={t('stang', { defaultValue: 'Stäng' })}
                onClick={props.onClose}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        props.onClose();
                    }
                }}
            />

            <div
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-label={t('en-eller-flera-kommuner') as string}
                tabIndex={-1}
                className="card bg-base-100 roundex-xl p-6 w-full max-w-md max-h-full pointer-events-auto outline-none"
            >
                <div className="mb-6 flex gap-3 justify-between">
                    <span>{t('en-eller-flera-kommuner')}</span>
                    {props.featureFilter.length > 0 && (
                        <button
                            onClick={props.onClear}
                            className="btn btn-xs btn-secondary"
                        >
                            {t('aterstall', { defaultValue: 'Återställ' })}
                        </button>
                    )}
                </div>
                <ul className="menu bg-base-100 h-96 max-h-full overflow-scroll no-scrollbar">
                    {props.kommuner
                        .sort((a, b) => a.localeCompare(b, 'sv'))
                        .map((kommun) => ({
                            id: kommun,
                            value: kommun,
                            label: kommun,
                        }))
                        .map((option) => (
                            <li key={option.id}>
                                <button
                                    className={`${
                                        props.featureFilter.includes(
                                            option.value
                                        )
                                            ? 'active'
                                            : undefined
                                    }`}
                                    onClick={() => {
                                        props.onClick(option.value);
                                    }}
                                >
                                    {option.label}
                                </button>
                            </li>
                        ))}
                </ul>
            </div>
        </div>
    );
}
